'use client';

import { useTranslations } from 'next-intl';
import { Trash2, Package, Monitor, Hash } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { WorkloadAssignment } from '../types';
import { AssignmentStatusBadge } from './assignment-status-badge';

interface WorkloadCardProps {
  assignment: WorkloadAssignment;
  onDelete: (assignment: WorkloadAssignment) => void;
  onClick?: (assignment: WorkloadAssignment) => void;
  avatarColor: string;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function WorkloadCard({
  assignment,
  onDelete,
  onClick,
  avatarColor
}: WorkloadCardProps) {
  const t = useTranslations('handoverScanning');

  const operatorName = assignment.operator?.name ?? '';
  const deviceName = assignment.device?.name ?? '';
  const containerNumber =
    assignment.scanning_container?.container_number ?? '—';

  return (
    <Card
      className={cn(
        'border-border hover:border-primary/30 transition-colors',
        onClick && 'cursor-pointer'
      )}
      onClick={() => onClick?.(assignment)}
    >
      <CardContent className='p-5'>
        {/* Top: assignment number + status badge */}
        <div className='mb-4 flex items-start justify-between'>
          <div>
            <p className='font-mono text-sm font-bold'>
              {assignment.assignment_number}
            </p>
            <p className='text-muted-foreground mt-0.5 text-xs'>
              {containerNumber}
            </p>
          </div>
          <AssignmentStatusBadge status={assignment.status} />
        </div>

        {/* Middle: operator, device, shipment, boxes */}
        <div className='mb-4 space-y-2.5'>
          {operatorName && (
            <div className='flex items-center gap-2'>
              <Avatar className='h-6 w-6 shrink-0'>
                <AvatarFallback
                  className={cn(
                    'text-[10px] font-bold text-white',
                    avatarColor
                  )}
                >
                  {getInitials(operatorName)}
                </AvatarFallback>
              </Avatar>
              <span className='text-sm'>{operatorName}</span>
            </div>
          )}
          {deviceName && (
            <div className='flex items-center gap-2'>
              <Monitor className='text-muted-foreground h-4 w-4 shrink-0' />
              <span className='text-muted-foreground text-sm'>
                {deviceName}
              </span>
            </div>
          )}
          {assignment.shipment_number && (
            <div className='flex items-center gap-2'>
              <Hash className='text-muted-foreground h-4 w-4 shrink-0' />
              <span className='text-muted-foreground text-sm'>
                {assignment.shipment_number}
              </span>
            </div>
          )}
          {assignment.boxes_count != null && (
            <div className='flex items-center gap-2'>
              <Package className='text-muted-foreground h-4 w-4 shrink-0' />
              <span className='text-muted-foreground text-sm'>
                {t('boxCountLabel', { count: assignment.boxes_count })}
              </span>
            </div>
          )}
        </div>

        {/* Bottom: action buttons */}
        <div className='flex items-center justify-end border-t pt-3'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
            onClick={(e) => {
              e.stopPropagation();
              onDelete(assignment);
            }}
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
